/**
 * Revenue Calculation Utilities for City Partnership Pages
 *
 * Provides consistent formulas for calculating municipal revenue projections
 * based on city-specific data (population, annual spending, adoption rates).
 */

export interface CityRevenueInputs {
  /** Total population of the city */
  population: number;
  /** Annual consumer spending (e.g., "$200-300B" or "$6-8B") */
  annualConsumerSpending: string;
  /** Cost per resident per month for single-city sponsorship (e.g., "$0.60") */
  residentCostSingleCity: string;
  /** Percentage of transaction volume city receives (e.g., "0.2%") */
  cityRevenueSharePercent: string;
  /** Percentage of transaction volume community treasury receives (e.g., "0.2%") */
  treasurySharePercent: string;
}

export interface AdoptionScenario {
  adoptionRate: number; // e.g., 0.20 for 20%
  adoptionLabel: string; // e.g., "20%"
  adoptionDescription: string; // e.g., "Conservative Adoption"
  transactionVolume: string; // e.g., "$1.2-1.6B/year"
  cityRevenue: string; // e.g., "$2.4-3.2M/year"
  treasuryRevenue: string; // e.g., "$2.4-3.2M/year"
  combinedBenefit: string; // e.g., "$4.8-6.4M/year"
}

export interface ROIScenario {
  adoptionRate: number;
  adoptionLabel: string;
  scenarioName: string; // e.g., "Conservative Scenario: 20% Vendor Adoption"
  transactionVolume: string;
  cityRevenue: string;
  citySponsorshipCost: string;
  netCityBenefit: string;
  roiMultiplier: string; // e.g., "4-5.5x"
  cardColorClass: string; // Tailwind classes for styling
}

/**
 * Parse spending string like "$200-300B" or "$6-8B" into numeric range
 * Handles shorthand notation where unit applies to both bounds (e.g., "$200-300B" → $200B-$300B)
 */
function parseSpendingRange(spending: string): { low: number; high: number } {
  const cleaned = spending.replace(/[$,\s]/g, '');
  const parts = cleaned.split('-');

  const parseValue = (val: string, inheritedUnit?: string): number => {
    // Extract unit from value (B, M, K) or use inherited unit
    const unitMatch = val.match(/([BMK])$/);
    const unit = unitMatch ? unitMatch[1] : inheritedUnit;
    const numStr = val.replace(/[BMK]$/, '');
    const num = parseFloat(numStr);

    if (unit === 'B') return num * 1_000_000_000;
    if (unit === 'M') return num * 1_000_000;
    if (unit === 'K') return num * 1_000;
    return num;
  };

  if (parts.length === 2) {
    // Extract unit from second part (e.g., "300B" → "B")
    const unitMatch = parts[1].match(/([BMK])$/);
    const unit = unitMatch ? unitMatch[1] : undefined;

    return {
      low: parseValue(parts[0], unit), // Inherit unit from high bound if missing
      high: parseValue(parts[1]),
    };
  } else {
    const val = parseValue(parts[0]);
    return { low: val, high: val };
  }
}

/**
 * Parse percentage string like "0.2%" into decimal (0.002)
 */
function parsePercentage(percent: string): number {
  return parseFloat(percent.replace('%', '')) / 100;
}

/**
 * Format large numbers into readable strings (e.g., 1200000000 → "$1.2B")
 */
function formatLargeNumber(num: number): string {
  if (num >= 1_000_000_000) {
    const billions = num / 1_000_000_000;
    return `$${billions.toLocaleString('en-US', { minimumFractionDigits: billions >= 10 ? 0 : 1, maximumFractionDigits: billions >= 10 ? 0 : 1 })}B`;
  } else if (num >= 1_000_000) {
    const millions = num / 1_000_000;
    return `$${millions.toLocaleString('en-US', { minimumFractionDigits: millions >= 10 ? 0 : 1, maximumFractionDigits: millions >= 10 ? 0 : 1 })}M`;
  } else if (num >= 1_000) {
    const thousands = num / 1_000;
    return `$${thousands.toLocaleString('en-US', { minimumFractionDigits: thousands >= 10 ? 0 : 1, maximumFractionDigits: thousands >= 10 ? 0 : 1 })}K`;
  }
  return `$${Math.round(num).toLocaleString('en-US')}`;
}

/**
 * Calculate transaction volume for a given adoption rate
 * DECISION: Use conservative (low) value only - removed range formatting
 * to improve clarity and reduce confusion from duplicate ranges.
 */
function calculateTransactionVolume(
  annualConsumerSpending: string,
  adoptionRate: number,
): { low: number; high: number; formatted: string } {
  const spending = parseSpendingRange(annualConsumerSpending);
  const low = spending.low * adoptionRate;
  const high = spending.high * adoptionRate;

  return {
    low,
    high,
    formatted: `${formatLargeNumber(low)}/year`,
  };
}

/**
 * Calculate city revenue from transaction volume
 * DECISION: Use conservative (low) value only - removed range formatting
 * to improve clarity and reduce confusion from duplicate ranges.
 */
function calculateCityRevenue(
  transactionVolume: { low: number; high: number },
  cityRevenueSharePercent: string,
): { low: number; high: number; formatted: string } {
  const sharePercent = parsePercentage(cityRevenueSharePercent);
  const low = transactionVolume.low * sharePercent;
  const high = transactionVolume.high * sharePercent;

  return {
    low,
    high,
    formatted: `${formatLargeNumber(low)}/year`,
  };
}

/**
 * Calculate annual city sponsorship cost
 */
export function calculateSponsorshipCost(
  population: number,
  residentCostSingleCity: string,
): { annual: number; formatted: string } {
  const monthlyCost = parseFloat(residentCostSingleCity.replace('$', ''));
  const annual = population * monthlyCost * 12;

  return {
    annual,
    formatted: `${formatLargeNumber(annual)}/year`,
  };
}

/**
 * Generate adoption scenarios for revenue projection table
 */
export function generateAdoptionScenarios(inputs: CityRevenueInputs): AdoptionScenario[] {
  const scenarios = [
    { rate: 0.2, label: '20%', description: 'Conservative Adoption' },
    { rate: 0.3, label: '30%', description: 'Moderate Adoption' },
    { rate: 0.5, label: '50%', description: 'High Adoption' },
  ];

  return scenarios.map((scenario) => {
    const transactionVolume = calculateTransactionVolume(
      inputs.annualConsumerSpending,
      scenario.rate,
    );
    const cityRevenue = calculateCityRevenue(transactionVolume, inputs.cityRevenueSharePercent);
    const treasuryRevenue = calculateCityRevenue(transactionVolume, inputs.treasurySharePercent);
    const combinedLow = cityRevenue.low + treasuryRevenue.low;
    const combinedHigh = cityRevenue.high + treasuryRevenue.high;

    return {
      adoptionRate: scenario.rate,
      adoptionLabel: scenario.label,
      adoptionDescription: scenario.description,
      transactionVolume: transactionVolume.formatted,
      cityRevenue: cityRevenue.formatted,
      treasuryRevenue: treasuryRevenue.formatted,
      combinedBenefit: `${formatLargeNumber(combinedLow)}/year`,
    };
  });
}

/**
 * Generate ROI scenarios for city-sponsored universal access model
 */
export function generateROIScenarios(inputs: CityRevenueInputs): ROIScenario[] {
  const sponsorshipCost = calculateSponsorshipCost(
    inputs.population,
    inputs.residentCostSingleCity,
  );

  // Calculate break-even rate using LOW spending (conservative baseline)
  const spending = parseSpendingRange(inputs.annualConsumerSpending);
  const sharePercent = parsePercentage(inputs.cityRevenueSharePercent);
  const breakEvenRate = sponsorshipCost.annual / (spending.low * sharePercent);

  const scenarios = [
    {
      rate: breakEvenRate,
      label: `~${(breakEvenRate * 100).toFixed(1)}%`,
      name: `Break-Even Scenario: ~${(breakEvenRate * 100).toFixed(1)}% Vendor Adoption`,
      colorClass: 'bg-amber-50 border-amber-300',
    },
    {
      rate: 0.2,
      label: '20%',
      name: 'Conservative Scenario: 20% Vendor Adoption',
      colorClass: 'bg-white border-green-200',
    },
    {
      rate: 0.3,
      label: '30%',
      name: 'Moderate Scenario: 30% Vendor Adoption',
      colorClass: 'bg-gradient-to-r from-green-100 to-emerald-100 border-green-400',
    },
    {
      rate: 0.5,
      label: '50%',
      name: 'High Adoption Scenario: 50% Vendor Adoption',
      colorClass: 'bg-gradient-to-r from-emerald-100 to-green-200 border-emerald-500',
    },
  ];

  return scenarios.map((scenario) => {
    const transactionVolume = calculateTransactionVolume(
      inputs.annualConsumerSpending,
      scenario.rate,
    );
    const cityRevenue = calculateCityRevenue(transactionVolume, inputs.cityRevenueSharePercent);

    const netBenefitLow = cityRevenue.low - sponsorshipCost.annual;
    const netBenefitHigh = cityRevenue.high - sponsorshipCost.annual;
    const roiLow = cityRevenue.low / sponsorshipCost.annual;
    const roiHigh = cityRevenue.high / sponsorshipCost.annual;

    return {
      adoptionRate: scenario.rate,
      adoptionLabel: scenario.label,
      scenarioName: scenario.name,
      transactionVolume: transactionVolume.formatted,
      cityRevenue: cityRevenue.formatted,
      citySponsorshipCost: sponsorshipCost.formatted,
      netCityBenefit: `${formatLargeNumber(netBenefitLow)}/year profit`,
      roiMultiplier: `${roiLow.toFixed(1)}x`,
      cardColorClass: scenario.colorClass,
    };
  });
}

/**
 * Calculate break-even adoption rate for city-sponsored model
 */
export function calculateBreakEvenAdoption(inputs: CityRevenueInputs): {
  adoptionRate: number;
  adoptionPercent: string;
  transactionVolume: string;
  description: string;
} {
  const sponsorshipCost = calculateSponsorshipCost(
    inputs.population,
    inputs.residentCostSingleCity,
  );
  const spending = parseSpendingRange(inputs.annualConsumerSpending);
  const sharePercent = parsePercentage(inputs.cityRevenueSharePercent);

  // Break-even calculation using LOW spending baseline (conservative)
  // This ensures the MINIMUM city revenue equals sponsorship cost
  // Formula: sponsorshipCost = (lowSpending * adoptionRate * sharePercent)
  // adoptionRate = sponsorshipCost / (lowSpending * sharePercent)
  const breakEvenRate = sponsorshipCost.annual / (spending.low * sharePercent);
  // Normalize to [0,1] range
  const normalizedRate = Math.max(0, Math.min(1, breakEvenRate));

  const transactionVolume = calculateTransactionVolume(
    inputs.annualConsumerSpending,
    normalizedRate,
  );

  return {
    adoptionRate: normalizedRate,
    adoptionPercent: `~${(normalizedRate * 100).toFixed(1)}%`,
    transactionVolume: transactionVolume.formatted,
    description: `Break-even calculated using low consumer spending baseline (${formatLargeNumber(spending.low)}). At ~${(normalizedRate * 100).toFixed(1)}% vendor adoption, minimum city revenue equals ${sponsorshipCost.formatted} sponsorship cost. At higher spending scenarios, city generates surplus from day one.`,
  };
}

/**
 * Calculate full mandate revenue (100% adoption of all local commerce)
 */
export function calculateFullMandateRevenue(inputs: CityRevenueInputs): {
  transactionVolume: string;
  cityRevenue: string;
  treasuryRevenue: string;
  combinedBenefit: string;
} {
  // 100% adoption = full annual consumer spending flows through platform
  const spending = parseSpendingRange(inputs.annualConsumerSpending);
  const sharePercent = parsePercentage(inputs.cityRevenueSharePercent);
  const treasurySharePercent = parsePercentage(inputs.treasurySharePercent);

  const cityRevenueLow = spending.low * sharePercent;
  const cityRevenueHigh = spending.high * sharePercent;
  const treasuryRevenueLow = spending.low * treasurySharePercent;
  const treasuryRevenueHigh = spending.high * treasurySharePercent;
  const combinedLow = cityRevenueLow + treasuryRevenueLow;
  const combinedHigh = cityRevenueHigh + treasuryRevenueHigh;

  return {
    transactionVolume: `${formatLargeNumber(spending.low)}/year`,
    cityRevenue: `${formatLargeNumber(cityRevenueLow)}/year`,
    treasuryRevenue: `${formatLargeNumber(treasuryRevenueLow)}/year`,
    combinedBenefit: `${formatLargeNumber(combinedLow)}/year`,
  };
}

/**
 * Calculate statewide network break-even rate (with discounted pricing)
 */
export function calculateStatewideBreakEven(
  inputs: CityRevenueInputs,
  statewideResidentCost: string,
): {
  adoptionPercent: string;
  transactionVolume: string;
  cityRevenue: string;
  cityCost: string;
  profitAtModerate: string;
} {
  const statewideSponsorship = calculateSponsorshipCost(inputs.population, statewideResidentCost);
  const spending = parseSpendingRange(inputs.annualConsumerSpending);
  const sharePercent = parsePercentage(inputs.cityRevenueSharePercent);

  // Calculate break-even adoption rate using LOW spending (conservative)
  const breakEvenRate = statewideSponsorship.annual / (spending.low * sharePercent);
  const normalizedRate = Math.max(0, Math.min(1, breakEvenRate));

  // Calculate profit at 30% moderate adoption using LOW spending (conservative)
  const moderateVolume = calculateTransactionVolume(inputs.annualConsumerSpending, 0.3);
  const moderateRevenue = calculateCityRevenue(moderateVolume, inputs.cityRevenueSharePercent);
  const profitLow = moderateRevenue.low - statewideSponsorship.annual;

  return {
    adoptionPercent: `~${(normalizedRate * 100).toFixed(1)}%`,
    transactionVolume: calculateTransactionVolume(inputs.annualConsumerSpending, normalizedRate)
      .formatted,
    cityRevenue: calculateCityRevenue(
      calculateTransactionVolume(inputs.annualConsumerSpending, normalizedRate),
      inputs.cityRevenueSharePercent,
    ).formatted,
    cityCost: statewideSponsorship.formatted,
    profitAtModerate: `${formatLargeNumber(profitLow)}/year profit`,
  };
}
